import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import produce from 'immer';

export interface GameModeModel {
  isStarted: boolean;
  teams: Team [];
  isTeamTalkOn: boolean;
  teacherOnTeamIndex: number;
  lastTeamId: number;
  teacherOnTeamId: number;
  participantsTeamIndexById: { [key: number]: number };
  scoreByTeamId: { [key: string]: number };
}

export interface Team {
  id: number;
  name: string;
  participantIds: number[];
  teamScore: number;
}

export namespace GameModeAction {
  export class UpdateTeamName {
    static readonly type = '[Game-mode] update team name';

    constructor(public teamIndex: number, public teamName: string) {
    }
  }

  export class StartGameMode {
    static readonly type = '[Game-mode] start game mode';

    constructor() {
    }
  }

  export class EndGameMode {
    static readonly type = '[Game-mode] end game mode';

    constructor() {
    }
  }

  export class ToggleTeamTalk {
    static readonly type = '[Game-mode] toggle team talk';

    constructor(public isEnabled: boolean) {
    }
  }

  export class TeacherSwitchToTeam {
    static readonly type = '[Game-mode] teacher switch to team';

    constructor(public teamId: number) {
    }
  }

  export class SetTeamScore {
    static readonly type = '[Game-mode] add team score';

    constructor(public teamId: number, public score: number) {
    }
  }

  export class TeacherLeaveTeam {
    static readonly type = '[Game-mode] teacher leave team';

    constructor() {
    }
  }

  export class UpdateTeamsInfo {
    static readonly type = '[Game-mode] update teams info';

    constructor(public teams: Team[], public lastCreatedTeamId: number) {
    }
  }

  export class ExcludeFromTeam {
    static readonly type = '[Game-mode] exclude user from team';

    constructor(public userId) {
    }
  }
}

@State<GameModeModel>({
  name: GameModeState.storeName,
  defaults: GameModeState.defaults
})
@Injectable()
export class GameModeState {
  static readonly storeName = 'gameMode';
  static readonly defaults: GameModeModel = {
    isStarted: false, teams: [
      {id: 1, name: 'Team 1', participantIds: [], teamScore: 0},
      {id: 2, name: 'Team 2', participantIds: [], teamScore: 0}
    ],
    isTeamTalkOn: false,
    teacherOnTeamIndex: null,
    lastTeamId: -1,
    // TODO - decide if not need remove
    teacherOnTeamId: null,
    participantsTeamIndexById: {},

    // eslint-disable-next-line @typescript-eslint/naming-convention
    scoreByTeamId: {1: 0, 2: 0},
  };

  @Selector()
  static getGameMode(gameMode: GameModeModel) {
    return gameMode;
  }

  @Action(GameModeAction.SetTeamScore)
  setTeamScore(ctx: StateContext<GameModeModel>, payload: GameModeAction.SetTeamScore) {
    ctx.setState(produce((state) => {
      state.scoreByTeamId[payload.teamId] = payload.score;
    }));
  }

  @Action(GameModeAction.UpdateTeamName)
  updateTeamName(ctx: StateContext<GameModeModel>, payload: GameModeAction.UpdateTeamName) {
    ctx.setState(produce((state) => {
      state.teams[payload.teamIndex].name = payload.teamName;
    }));
  }

  @Action(GameModeAction.StartGameMode)
  startGameMode(ctx: StateContext<GameModeModel>) {
    const state = ctx.getState();
    ctx.patchState({...state, isStarted: true, isTeamTalkOn: false});
  }

  @Action(GameModeAction.EndGameMode)
  endGameMode(ctx: StateContext<GameModeModel>) {
    ctx.patchState(GameModeState.defaults);
  }

  @Action(GameModeAction.ToggleTeamTalk)
  toggleTeamTalk(ctx: StateContext<GameModeModel>, payload: GameModeAction.ToggleTeamTalk) {
    const state = ctx.getState();
    if (!payload.isEnabled) {
      ctx.patchState({...state, isTeamTalkOn: payload.isEnabled, teacherOnTeamIndex: null, teacherOnTeamId: null});
    } else {
      ctx.patchState({...state, isTeamTalkOn: payload.isEnabled});
    }
  }


  @Action(GameModeAction.TeacherSwitchToTeam)
  teacherSwitchToTeam(ctx: StateContext<GameModeModel>, payload: GameModeAction.TeacherSwitchToTeam) {
    const state = ctx.getState();
    const teamIndex = state.teams.findIndex(team => team.id === payload.teamId);
    ctx.patchState({...state, teacherOnTeamId: payload.teamId, teacherOnTeamIndex: teamIndex});
  }

  @Action(GameModeAction.TeacherLeaveTeam)
  teacherLeaveTeam(ctx: StateContext<GameModeModel>) {
    const state = ctx.getState();
    ctx.patchState({...state, teacherOnTeamId: null, teacherOnTeamIndex: null});
  }

  @Action(GameModeAction.UpdateTeamsInfo)
  updateTeamsInfo(ctx: StateContext<GameModeModel>, payload: GameModeAction.UpdateTeamsInfo) {
    ctx.setState(produce((state) => {
      state.teams = payload.teams;
      state.lastTeamId = payload.lastCreatedTeamId;
      state.participantsTeamIndexById = {};
      const currentTeamIds: number[] = [];

      for (let i = 0; i < state.teams.length; i++) {
        const team: Team = state.teams[i];

        currentTeamIds.push(team.id);
        if (state.scoreByTeamId[team.id] === undefined) {
          state.scoreByTeamId[team.id] = 0;
        }

        const teamParticipantIds: number[] = team.participantIds;

        for (const id of teamParticipantIds) {
          state.participantsTeamIndexById[id] = i;
        }
      }

      for (const teamId in state.scoreByTeamId) {
        if (currentTeamIds.indexOf(parseInt(teamId)) === -1) {
          delete state.scoreByTeamId[teamId];
        }
      }

      if (state.teacherOnTeamId !== null) {
        if (currentTeamIds.indexOf(state.teacherOnTeamId) === -1) {
          state.teacherOnTeamId = null;
          state.teacherOnTeamIndex = null;
        } else {
          state.teacherOnTeamIndex = state.teams.findIndex(team => team.id === state.teacherOnTeamId);
        }
      }
    }));
  }

  @Action(GameModeAction.ExcludeFromTeam)
  excludeFromTeam(ctx: StateContext<GameModeModel>, {userId}: GameModeAction.ExcludeFromTeam) {
    const state: GameModeModel = Object.assign({}, ctx.getState());
    const teamIndexById: {[key: number]: number} = {};
    Object.keys(state.participantsTeamIndexById).forEach((id: string) => {
      if (parseInt(id) !== userId) {
        teamIndexById[id] = state.participantsTeamIndexById[id];
      }
    });

    state.teams.forEach((team: Team) => {
      team.participantIds.filter((id: number) => id !== userId);
    });

    ctx.patchState({
      participantsTeamIndexById: teamIndexById,
      teams: state.teams
    });
  }
}
